'use client';

import { Footprints, PlayCircle, ListChecks } from 'lucide-react';
import { useGuidedTour } from './GuidedTourProvider';
import { resolveFlowFormats, TOUR_FORMATS, type TourFormat } from './utils/formatResolver';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export function GuidedTourFormatPicker() {
  const { engine, selectedFlow } = useGuidedTour();

  if (!selectedFlow || engine.viewMode !== 'format-picker') {
    return null;
  }

  const resolution = resolveFlowFormats(selectedFlow, engine.currentFormat);

  const formatIcon = (icon: string) => {
    switch (icon) {
      case 'play-circle':
        return <PlayCircle className="h-4 w-4" />;
      case 'list-checks':
        return <ListChecks className="h-4 w-4" />;
      case 'footprints':
      default:
        return <Footprints className="h-4 w-4" />;
    }
  };

  return (
    <div className="pointer-events-auto fixed bottom-4 right-4 z-[70] w-[min(92vw,24rem)]">
      <Card className="border-slate-200/80 bg-white/95 shadow-2xl backdrop-blur-md dark:border-slate-700/80 dark:bg-slate-800/95">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Choose a format</CardTitle>
          <p className="text-muted-foreground text-xs">
            How would you like to learn <strong>{selectedFlow.label}</strong>?
          </p>
        </CardHeader>
        <CardContent className="space-y-2 pt-0">
          {resolution.availableFormats.map((format) => {
            const info = TOUR_FORMATS.find((f) => f.id === format)!;
            const isSelected = engine.currentFormat === format;

            return (
              <Button
                key={format}
                variant={isSelected ? 'default' : 'outline'}
                className="h-auto w-full justify-start gap-3 px-4 py-3 text-left"
                onClick={() => engine.setCurrentFormat(format)}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                  {formatIcon(info.icon)}
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-semibold dark:text-slate-100">{info.label}</span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400">{info.description}</span>
                </span>
              </Button>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
